import type {
  MarkupCalculatorArgs,
  MarkupCalculatorResponse,
  PriceToYieldArgs,
  PriceToYieldResponse,
  YieldToPriceArgs,
  YieldToPriceResponse,
} from '@/types/api/analytics'
import type {
  PortfolioStatsRequest,
  PortfolioStatsResponse,
} from '@/types/api/portfolio-stats'

import { toError } from '@moment/observability/logger'

import { createLogger } from '@/lib/logger'
import { TagTypes } from '@/types/api/tag-types'

import { baseApiSlice } from './base-api-slice'

const logger = createLogger('api/analytics-slice')

const analyticsSlice = baseApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPriceToYield: builder.query<PriceToYieldResponse, PriceToYieldArgs>({
      query: ({ instrumentId, price, settlementDate }) => ({
        url: `analytics/${instrumentId}/price-to-yield/`,
        method: 'GET',
        params: {
          price,
          ...(settlementDate && { settlement_date: settlementDate }),
        },
      }),
      providesTags: [TagTypes.Analytics],
      async onQueryStarted({ instrumentId, price }, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (rejection) {
          logger.error('Failed to price yield', {
            error: toError(rejection),
            instrumentId,
            price,
          })
        }
      },
    }),
    getYieldToPrice: builder.query<YieldToPriceResponse, YieldToPriceArgs>({
      query: ({ instrumentId, yieldToWorst }) => ({
        url: `analytics/${instrumentId}/yield-to-price/`,
        method: 'GET',
        params: { yield_to_worst: yieldToWorst },
      }),
      providesTags: [TagTypes.Analytics],
    }),
    getMarkupAdjustment: builder.query<
      MarkupCalculatorResponse,
      MarkupCalculatorArgs
    >({
      query: ({ instrumentId, price, riskGroupId, side, leg }) => ({
        url: `analytics/${instrumentId}/markup-calculator/`,
        method: 'GET',
        params: { price, risk_group_id: riskGroupId, side, leg },
      }),
      providesTags: [TagTypes.Analytics],
    }),
    getPortfolioStats: builder.mutation<
      PortfolioStatsResponse,
      PortfolioStatsRequest
    >({
      query: (body) => ({
        url: `analytics/portfolio-stats/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [TagTypes.Analytics],
    }),
  }),
})

export const {
  endpoints: {
    getPriceToYield,
    getYieldToPrice,
    getMarkupAdjustment,
    getPortfolioStats,
  },
  useGetPriceToYieldQuery,
  useLazyGetPriceToYieldQuery,
  useGetYieldToPriceQuery,
  useLazyGetYieldToPriceQuery,
  useGetMarkupAdjustmentQuery,
  useGetPortfolioStatsMutation,
} = analyticsSlice
